import React from 'react';
import styled from 'styled-components';
import toast from 'toasted-notes';
import 'toasted-notes/src/styles.css';
import colors from '@utils/colors';
import MaterialIcon from '../icons/MaterialIcon';
import { softCloseHover } from '../dialog/softCloseHover';
import storage from '@utils/storage';
import { mixHex } from '@utils/theme/softBackground';

export enum NotificationMode {
  ERROR = 'error',
  ALERT = 'alert',
  SUCCESS = 'success',
  INFO = 'info'
}

/**
 * The top toasts (Petr's pick, 2026-10-30, "A + C, coloured text"): a soft wash of the kind's colour over the page's own
 * surface, so they follow the background and dark mode, with a round icon a shade stronger and the text in a deep
 * version of the colour. Done is the app's purple, a side note grey, a warning amber, an error red. The Undo bar at the
 * bottom stays dark, apart from these.
 */
const toastKinds = {
  [NotificationMode.SUCCESS]: { light: colors.primaryPurple, dark: mixHex(colors.primaryPurple, colors.white, 0.25) },
  [NotificationMode.INFO]: { light: colors.gray, dark: '#AEB6C2' },
  [NotificationMode.ALERT]: { light: '#E39A12', dark: colors.warning },
  [NotificationMode.ERROR]: { light: '#F04B5B', dark: '#FF7B88' }
};

// The icon in each kind's dot: a bare glyph, as the dot is its ring; a note's is a letter, which Material Icons lacks.
const toastGlyphs = {
  [NotificationMode.SUCCESS]: 'check',
  [NotificationMode.INFO]: null,
  [NotificationMode.ALERT]: 'priority_high',
  [NotificationMode.ERROR]: 'close'
};

// Something already said why (a failed save, the offline bar; utils/maintenance.ts): the general "Something went wrong"
// right after only repeats it.
const REPEAT_MS = 2000;
let lastErrorAt = 0;

/** Something else has just told the student what went wrong (the offline bar, say). */
export const muteDefaultError = () => {
  lastErrorAt = Date.now();
};

const getDefaultMessage = (mode: NotificationMode) => {
  if (mode === NotificationMode.ERROR && Date.now() - lastErrorAt < REPEAT_MS) return undefined;
  if (mode === NotificationMode.ERROR) return 'Something went wrong, please reload and try again.';
  if (mode === NotificationMode.SUCCESS) return 'Success!';
  return undefined;
};

export const alert = (
  message?: string,
  mode: NotificationMode = NotificationMode.ERROR,
  duration: number | null = 3000,
  closable: boolean = true,
  action?: { label: string; onClick: () => void }
) => {
  const text = message || getDefaultMessage(mode);
  if (text && mode === NotificationMode.ERROR) lastErrorAt = Date.now();
  const dark = storage.getTheme() !== 'light';
  const kind = dark ? toastKinds[mode].dark : toastKinds[mode].light;
  const ink = dark ? mixHex(kind, colors.white, 0.6) : mixHex(kind, '#1D2531', 0.48);
  const mark = dark ? mixHex(kind, colors.white, 0.3) : kind;
  const glyph = toastGlyphs[mode];
  text &&
    toast.notify(
      ({ onClose }) => (
        <Toast $kind={kind} $ink={ink} $dark={dark}>
          <Dot $kind={kind} $mark={mark} $dark={dark}>
            {glyph ? <MaterialIcon name={glyph} size={15} color={mark} /> : <NoteGlyph>i</NoteGlyph>}
          </Dot>
          <ToastText>{text}</ToastText>
          {action && (
            <ActionButton
              type="button"
              color={ink}
              onClick={() => {
                action.onClick();
                onClose();
              }}
            >
              {action.label}
            </ActionButton>
          )}
          {closable && <ToastClose name={'close'} size={16} color={ink} onClick={onClose} />}
        </Toast>
      ),
      { duration }
    );
};

// Clear of the onboarding badges bar (bottom 60px, 40px tall) while it shows, else just off the edge.
const bottomOffset = () => (document.querySelector('[data-onboarding-bar="visible"]') ? 112 : 24);

// The bottom spot holds one toast. Two Undo bars stacked read as two changes to choose from, yet Cmd+Z only reaches the
// latest (Petr, 2026-10-09); so a new toast fades the one before it out as it comes in.
let closeCurrentBottomToast: (() => void) | undefined;

/**
 * The Undo toast (Petr's pick, 2026-10-09): dark and at the bottom, apart from the white messages at the top, so a dark
 * bar reads as "you can take this back". The shortcut sits next to the button, which is how students learn it. What
 * follows an undo ("Task restored") shows in the same spot and look.
 */
export const bottomToast = ({
  message,
  icon,
  action,
  shortcut,
  duration = 3000
}: {
  message: string;
  icon?: string;
  action?: { label: string; onClick: () => void };
  shortcut?: string;
  duration?: number;
}) => {
  closeCurrentBottomToast?.();
  const dark = storage.getTheme() !== 'light';
  // A fading toast still takes clicks; the button acts once.
  let acted = false;
  const shown = toast.notify(
    ({ onClose }) => (
      <DarkSnackbar dark={dark} offset={bottomOffset()}>
        {icon && <DarkIcon name={icon} color={TOAST_ACCENT} size={16} cursor="default" />}
        <DarkText>{message}</DarkText>
        {action && (
          <DarkAction
            type="button"
            onClick={() => {
              if (acted) return;
              acted = true;
              action.onClick();
              onClose();
            }}
          >
            {action.label}
          </DarkAction>
        )}
        {action && shortcut && <KeyHint dark={dark}>{shortcut}</KeyHint>}
        <UndoClose name={'close'} color={UNDO_CLOSE_COLOR} size={14} onClick={onClose} />
      </DarkSnackbar>
    ),
    { duration, position: 'bottom' }
  );
  const close = () => {
    if (!shown) return;
    toast.close(shown.id, shown.position);
    // Closed straight after it was shown (Cmd+Z at once), it may not be in the toast list yet; once more after that.
    window.setTimeout(() => toast.close(shown.id, shown.position), 100);
  };
  closeCurrentBottomToast = close;
  return close;
};

// On logout or a switch of term: an Undo from before has nothing left to act on.
export const closeBottomToast = () => closeCurrentBottomToast?.();

/**
 * A session ping while the student is looking at Shovel (notifications cleanup, Petr 2026-10-31): the top toasts' look,
 * in the app's purple, over two lines. The whole card opens the session; the name can be any length, so it's cut on its
 * own line and the title always reads whole.
 */
export const notify = ({ title, body, onClick }: { title: string; body: string; onClick: () => void }) => {
  const dark = storage.getTheme() !== 'light';
  const kind = dark ? toastKinds[NotificationMode.SUCCESS].dark : toastKinds[NotificationMode.SUCCESS].light;
  const ink = dark ? mixHex(kind, colors.white, 0.6) : mixHex(kind, '#1D2531', 0.48);
  const mark = dark ? mixHex(kind, colors.white, 0.3) : kind;
  toast.notify(
    ({ onClose }) => (
      <PingToast
        $kind={kind}
        $ink={ink}
        $dark={dark}
        onClick={() => {
          onClick();
          onClose();
        }}
      >
        <Dot $kind={kind} $mark={mark} $dark={dark}>
          <MaterialIcon name={'schedule'} size={15} color={mark} />
        </Dot>
        <PingWords>
          <PingTitle>{title}</PingTitle>
          <PingName>{body}</PingName>
        </PingWords>
        <ToastClose
          name={'close'}
          size={16}
          color={ink}
          onClick={event => {
            event.stopPropagation();
            onClose();
          }}
        />
      </PingToast>
    ),
    { duration: 10000, position: 'top-right' }
  );
};

// On one line, wrapping only when the screen is too narrow for it (phones).
const Toast = styled.div<{ $kind: string; $ink: string; $dark: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  padding: 8px 12px 8px 8px;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, ${props => props.$kind} 30%, transparent);
  background: color-mix(
    in srgb,
    ${props => props.$kind} ${props => (props.$dark ? 20 : 10)}%,
    var(--color-surface, ${props => (props.$dark ? '#2C2F34' : colors.white)})
  );
  box-shadow: ${props =>
    props.$dark ? '0 8px 24px rgba(0, 0, 0, 0.45)' : '0 6px 20px rgba(63, 78, 97, 0.13), 0 1px 3px rgba(63, 78, 97, 0.08)'};
  color: ${props => props.$ink};
  font-family: 'Quicksand', sans-serif;
  white-space: nowrap;
`;

const Dot = styled.span<{ $kind: string; $mark: string; $dark: boolean }>`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: color-mix(
    in srgb,
    ${props => props.$kind} ${props => (props.$dark ? 38 : 24)}%,
    var(--color-surface, ${props => (props.$dark ? '#2C2F34' : colors.white)})
  );
  color: ${props => props.$mark};
`;

// A soft deepening of its own tint on hover, as the whole card is the button.
const PingToast = styled(Toast)`
  cursor: pointer;
  max-width: 340px;

  &:hover {
    background: color-mix(
      in srgb,
      ${props => props.$kind} ${props => (props.$dark ? 28 : 16)}%,
      var(--color-surface, ${props => (props.$dark ? '#2C2F34' : colors.white)})
    );
  }
`;

const PingWords = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
`;

const PingTitle = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.3;
`;

const PingName = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NoteGlyph = styled.span`
  font-family: Georgia, serif;
  font-size: 14px;
  font-style: italic;
  font-weight: 700;
  line-height: 1;
`;

const ToastText = styled.span`
  min-width: 0;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.3;
  white-space: normal;
`;

const ToastClose = styled(MaterialIcon)`
  flex-shrink: 0;
  margin-left: 2px;
  opacity: 0.5;
  // The X takes each toast's own ink, so the square does too.
  ${softCloseHover({ tone: 'currentColor', strong: 'inherit', over: 'transparent', amount: 18 })}

  &:hover {
    opacity: 0.9;
  }
`;

const DarkSnackbar = styled.div<{ dark: boolean; offset: number }>`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: ${props => props.offset}px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${props => toastBackground(props.dark)};
  border: 1px solid ${props => toastBorder(props.dark)};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  white-space: nowrap;
`;

// Wraps only when the screen is too narrow for it (phones).
const DarkText = styled.span`
  color: ${colors.white};
  font-size: 13px;
  white-space: normal;
  min-width: 0;
`;

// The Undo toast's colours, which the calendar's Undo button shares (Petr, 2026-10-24).
export const TOAST_ACCENT = '#a9a3ff';
export const toastBackground = (dark: boolean) => (dark ? '#3B3858' : '#2E2B45');
export const toastBorder = (dark: boolean) => (dark ? '#4E4A6E' : '#2E2B45');

const UNDO_CLOSE_COLOR = '#9c98b8';

const UndoClose = styled(MaterialIcon)`
  ${softCloseHover({ tone: UNDO_CLOSE_COLOR, strong: colors.white, over: 'transparent', amount: 24, spread: 3 })}
`;

const DarkIcon = styled(MaterialIcon)`
  margin-right: -6px;
`;

const DarkAction = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${TOAST_ACCENT};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const KeyHint = styled.span<{ dark: boolean }>`
  margin-left: -6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: ${props => (props.dark ? '#524E72' : '#46425F')};
  color: #d9d6f5;
  font-size: 11px;
  line-height: 16px;
`;

// Same size and weight as the message, so it reads as part of the toast rather than a separate banner.
const ActionButton = styled.button<{ color: string }>`
  padding: 2px 0 2px 12px;
  margin-left: 4px;
  border: none;
  border-left: 1px solid ${props => props.color}55;
  background: none;
  color: ${props => props.color};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
